import React, { useCallback, useMemo, useState, useEffect } from "react";
import Fuse from "fuse.js";
import * as R from "ramda";
import styled from "styled-components";
import { Avatar, Checkbox, Col, Empty, Icon, List, Modal, Row } from "antd";
import { useTranslation } from "react-i18next";

import SearchFilter from "../../scenes/Opportunity/routes/OpportunityCandidates/filters/SearchFilter"; // TODO move this to components
import { BREAKPOINT_NUMBERS } from "../../constants/sizeBreakpoints";
import Button from "../Button";

const StyledList = styled(List)`
  .ant-list-item {
    cursor: pointer;
    display: flex;
    flex-direction: row-reverse;
    margin: 0;
  }
`;

const StyledListItem = styled(List.Item)`
  background-color: ${({ checked }) => (checked ? "transparent" : "#f7f9fa")};
  border: ${({ checked, theme }) =>
      checked ? theme["@primary"] : "transparent"}
    solid 1px !important;
  box-shadow: ${({ checked }) =>
    checked ? "0px 3px 12px #00000029;" : "none"};
  height: 80px;
  display: flex !important;
  transition: background-color 0.4s ease, border 0.4s ease;
  :hover {
    border-color: ${({ theme }) => theme["@primary"]};
  }

  .ant-list-item-meta-title {
    color: #343a40;
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 4px;
  }

  .ant-list-item-meta-description {
    color: #747c84;
    font-size: 9px;
  }
`;

const Title = styled.h2`
  margin: 0;
`;

function SelectDataModal({
  cancelText,
  cancelButtonProps = {},
  data,
  emptyText,
  loading,
  okText,
  okButtonProps = {},
  onChange,
  onOk,
  onCancel,
  title,
  searchProps = [""],
  visible
}) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (searchText && !visible) setSearchText("");
  }, [searchText, visible]);

  const handleChange = useCallback(
    ({ id, checked }) => onChange({ id, checked }),
    [onChange]
  );

  const fuse = useMemo(
    () =>
      data &&
      new Fuse(data, {
        keys: searchProps
      }),
    [data, searchProps]
  );

  const filteredData = useMemo(() => fuse && fuse.search(searchText), [
    fuse,
    searchText
  ]);

  const empty = useMemo(
    () => (
      <Empty
        style={{ fontSize: 64 }}
        image={<Icon type="frown" />}
        imageStyle={{
          height: 64
        }}
        description={
          <span
            style={{
              fontSize: 14
            }}
          >
            {emptyText}
          </span>
        }
      />
    ),
    [emptyText]
  );

  return (
    <Modal
      centered
      bodyStyle={{ padding: 16, maxHeight: 500, overflowY: "scroll" }}
      closable={false}
      destroyOnClose
      title={
        <Row align="middle" type="flex" justify="space-between">
          <Col span={12}>
            <Title>{title}</Title>
          </Col>
          <Col span={10}>
            <SearchFilter
              searchText={searchText}
              setSearchText={setSearchText}
            />
          </Col>
        </Row>
      }
      footer={
        <>
          <Button onClick={onCancel} {...cancelButtonProps} type="quaternary">
            {cancelText || t("global:return")}
          </Button>
          <Button
            {...okButtonProps}
            onClick={onOk}
            disabled={
              (okButtonProps && okButtonProps.disabled) ||
              loading ||
              !data ||
              R.isEmpty(R.filter(R.propEq("checked", true), data))
            }
            type="primary"
          >
            {okText || t("global:continue")}
          </Button>
        </>
      }
      visible={visible}
      width={BREAKPOINT_NUMBERS.md}
    >
      {data ? (
        !loading && R.isEmpty(searchText ? filteredData : data) ? (
          empty
        ) : (
          <StyledList
            locale={{
              emptyText
            }}
            bordered={false}
            dataSource={searchText ? filteredData : data}
            grid={{ gutter: [0, 0] }}
            itemLayout="horizontal"
            loading={loading}
            renderItem={item => (
              <StyledListItem
                data-test={item.testKey}
                checked={item.checked}
                onClick={() => handleChange(item)}
                extra={
                  <Checkbox
                    data-test={`${item.testKey}-checkbox`}
                    style={{ padding: "0 24px" }}
                    checked={item.checked}
                    onChange={() => handleChange(item)}
                  />
                }
              >
                <List.Item.Meta
                  avatar={
                    <Avatar
                      style={{
                        backgroundColor: "transparent",
                        color: "#747D84",
                        fontSize: 24,
                        paddingRight: 24
                      }}
                      icon="file-done"
                    />
                  }
                  description={item.description}
                  style={{ alignItems: "center", display: "flex" }}
                  title={item.title}
                />
              </StyledListItem>
            )}
          />
        )
      ) : (
        empty
      )}
    </Modal>
  );
}

export default SelectDataModal;
